// src/utils/apiInterceptor.js
import { toast } from 'react-toastify';
import AuthService from '../Api/AuthService';
import { ServiceProvider } from '../Api/ServiceProvider';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const showFullScreenLoader = () => {
    const loader = document.createElement('div');
    loader.id = 'token-refresh-loader';
    loader.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      backdrop-filter: blur(4px);
    ">
      <div style="
        width: 60px;
        height: 60px;
        border: 4px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      "></div>
      <p style="
        color: white;
        margin-top: 20px;
        font-size: 16px;
        font-family: Arial, sans-serif;
      ">Refreshing session...</p>
    </div>
    <style>
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    </style>
  `;
    document.body.appendChild(loader);
};

const hideFullScreenLoader = () => {
    const loader = document.getElementById('token-refresh-loader');
    if (loader) {
        loader.remove();
    }
};

export const tokenRefresh = async () => {
    try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');

        if (!userData.refreshToken) {
            throw new Error('No refresh token available');
        }

        console.log('Attempting token refresh...');
        console.log('Endpoint:', ServiceProvider.auth.new);
        console.log('Refresh token exists:', !!userData.refreshToken);

        const response = await fetch(ServiceProvider.auth.new, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userData.refreshToken}`,
            },
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));

        // Check if response is OK before parsing
        if (!response.ok) {
            // Try to get error details from response
            let errorMessage = `Token refresh failed with status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || JSON.stringify(errorData);
            } catch (parseError) {
                // If JSON parsing fails, try to get text
                try {
                    const errorText = await response.text();
                    errorMessage = errorText || errorMessage;
                } catch (textError) {
                    // Ignore text parsing error
                }
            }
            throw new Error(errorMessage);
        }

        // Check content type to ensure it's JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            const textResponse = await response.text();
            console.error('Unexpected response type:', contentType);
            console.error('Response body:', textResponse);
            throw new Error(`Expected JSON response but got: ${contentType || 'unknown'}`);
        }

        let newTokenData;
        try {
            newTokenData = await response.json();
            console.log('Token refresh successful:', {
                hasAccessToken: !!newTokenData.accessToken,
                hasExpireDate: !!newTokenData.access_expire_date,
                keys: Object.keys(newTokenData)
            });
        } catch (jsonError) {
            console.error('Failed to parse JSON response:', jsonError);
            const rawResponse = await response.text().catch(() => 'Unable to read response');
            console.error('Raw response:', rawResponse);
            throw new Error('Invalid JSON response from token refresh endpoint');
        }

        const updatedUser = {
            ...userData,
            accessToken: newTokenData.accessToken,
            access_expire_date: newTokenData.access_expire_date,
        };

        const branchId = userData.branch?.branch_id;
        if (branchId) {
            try {
                const branchRes = await AuthService.selectBranch(branchId, newTokenData.accessToken);
                const finalData = { ...updatedUser, accessToken: branchRes.accessToken };
                localStorage.setItem("user", JSON.stringify(finalData));
                return finalData.accessToken;
            } catch (branchError) {
                console.error('Branch selection failed:', branchError);
                // If branch selection fails, still save the new token but log the error
                localStorage.setItem("user", JSON.stringify(updatedUser));
                return updatedUser.accessToken;
            }
        } else {
            localStorage.setItem("user", JSON.stringify(updatedUser));
            return updatedUser.accessToken;
        }
    } catch (error) {
        console.error('Token refresh error:', error);
        localStorage.removeItem('user');
        window.location.href = '/login';
        toast.error(error.message || 'Session expired. Please login again.');
        throw error;
    }
};

export const apiCall = async (url, options = {}) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');

    // Add authorization header if token exists
    if (userData.accessToken) {
        options.headers = {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userData.accessToken}`,
        };
    }

    try {
        let response = await fetch(url, options);

        // Check if response is 401
        if (response.status === 401) {
            const errorData = await response.json();

            // Check for specific error message
            if (
                errorData.status === 'FAIL' &&
                errorData.statusCode === 401 &&
                (errorData.error === 'Session expired or invalid' || errorData.message === "Invalid Refresh Token")
            ) {
                // If already refreshing, queue this request
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then(token => {
                            options.headers.Authorization = `Bearer ${token}`;
                            return fetch(url, options);
                        })
                        .catch(err => {
                            return Promise.reject(err);
                        });
                }

                isRefreshing = true;
                showFullScreenLoader();

                try {
                    const newToken = await tokenRefresh();
                    processQueue(null, newToken);

                    // Retry original request with new token
                    options.headers.Authorization = `Bearer ${newToken}`;
                    response = await fetch(url, options);

                    return response;
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    throw refreshError;
                } finally {
                    isRefreshing = false;
                    hideFullScreenLoader();
                }
            } else {
                // Different 401 error - logout
                localStorage.removeItem('user');
                localStorage.removeItem('user_rem');
                toast.error('Session expired. Please login again.', { autoClose: 3000 });
                window.location.href = '/login';
                throw new Error('Unauthorized');
            }
        }

        return response;
    } catch (error) {
        console.error('API call error:', error);
        throw error;
    }
};

export default apiCall;